import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import colorsStore from "shared/store/colorsStore";
import styles from "./monthlyDistributionChartCompare.module.scss";
import { parseMonths } from "utils/misc";

type MonthlyDistributionCompareProps = {
  data: Array<{ label: string; weight: number }>;
  data2: Array<{ label: string; weight: number }>;
  label: string;
};

const MonthlyDistributionChartCompare: React.FC<
  MonthlyDistributionCompareProps
> = ({ data, data2, label }) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  // Combine both datasets and find the maximum weight
  const allWeights = [
    ...data.map((item) => item.weight),
    ...data2.map((item) => item.weight),
  ];
  const maxWeight = Math.max(...allWeights);
  const buffer = 0.05; // Fixed buffer of 0.05
  const adjustedMaxWeight = Math.ceil((maxWeight + buffer) * 10) / 10; // Round to next 0.1

  // Format data for the chart combining both datasets
  const formattedData = data.map((item, index) => ({
    month: item.label,
    "Portafoglio 1": item.weight,
    "Portafoglio 2": data2[index].weight,
  }));

  const CustomLegend = (props) => {
    const { payload } = props;
    const colorsConfig = colorsStore.getState().colorsConfigStore;

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "38px",
          paddingTop: 10,
        }}
      >
        {payload.map((entry, index) => (
          <div
            key={`item-${index}`}
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: entry.color,
              }}
            />
            <span style={{ fontSize: 11, color: colorsConfig.finecoBlack }}>
              {entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  };
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div
          className={styles.tooltipContainer}
          style={{
            backgroundColor: "white",
            border: `1px solid ${colorsConfig.FinecoGrayLight1}`,
            padding: "8px 10px",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          {parseMonths(payload[0].payload.month)}
          {payload.map((entry: any, index: number) => {
            // if (entry.value === undefined) return null;
            return (
              <div key={`value-${index}`}>
                <p
                  style={{
                    color: entry.fill,
                    margin: "3px 0",
                    fontSize: "12px",
                  }}
                >
                  {entry.name}: {entry.payload["Portafoglio 1"]}
                </p>
                <p
                  style={{
                    color: entry.fill,
                    margin: "3px 0",
                    fontSize: "12px",
                  }}
                ></p>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };
  return (
    <div className={styles.container}>
      <div className={styles.monthlyDistributionTitle}>{label}</div>
      <ResponsiveContainer width="100%" height="100%" debounce={500}>
        <BarChart
          data={formattedData}
          margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
          barGap={4}
          barCategoryGap="10%"
        >
          <CartesianGrid
            stroke={colorsConfig.Gray3}
            vertical={false}
            horizontal={false}
          />
          <XAxis
            dataKey="month"
            tick={{ fill: colorsConfig.finecoBlack, fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: colorsConfig.Gray3 }}
          />
          <YAxis
            domain={[0, adjustedMaxWeight]}
            ticks={Array.from(
              { length: Math.ceil(adjustedMaxWeight / 0.05) + 1 },
              (_, i) => Number((i * 0.05).toFixed(2)),
            )}
            tick={{ fill: colorsConfig.finecoBlack, fontSize: 12 }}
            tickLine={false}
            axisLine={{ stroke: colorsConfig.Gray3 }}
            tickFormatter={(value) => value.toFixed(2)}
          />
          {/* <Tooltip
            formatter={(value: number) => [value.toFixed(2), "Distribuzione"]}
            cursor={{ fill: "transparent" }}
          /> */}
          <Tooltip content={<CustomTooltip />} />
          <Legend content={<CustomLegend />} />
          <Bar
            name="Portafoglio 1"
            dataKey="Portafoglio 1"
            fill={colorsConfig.portfolio1}
            radius={[2, 2, 0, 0]}
            isAnimationActive={false}
          />
          <Bar
            name="Portafoglio 2"
            dataKey="Portafoglio 2"
            fill={colorsConfig.portfolio2}
            radius={[2, 2, 0, 0]}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MonthlyDistributionChartCompare;
